import DoneIcon from '@mui/icons-material/Done';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import SelectDateAndTimeForm from '@components/SelectDateAndTimeForm/SelectDateAndTimeForm';
import { UserEvent } from '@features/events';
import { useUpdateInterview } from '@features/events/hooks/useUpdateInterview';
import { darkBlue2, white } from '@styles/colors';
import CloseIcon from '@mui/icons-material/Close';
import { OpenInterview } from '../../../../vectors/index';
import SelectDateTimeForm from '../SelectDateTimeForm/SelectDateTimeForm';

type UpdateInterviewInvitationModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  userEvent: UserEvent;
  onSuccess: () => void;
};

const UpdateInterviewInvitationModal: FC<
  UpdateInterviewInvitationModalProps
> = ({ isOpen, handleClose, userEvent, onSuccess }) => {
  const [startTimestamp, setStartTimestamp] = useState<number | null>(
    dayjs(userEvent.event.dt_start_time).valueOf() / 1000 ?? null,
  );
  const [endTimestamp, setEndTimestamp] = useState<number | null>(
    dayjs(userEvent.event.dt_end_time).valueOf() / 1000 ?? null,
  );
  const [formattedDate, setFormattedDate] = useState<string>(
    dayjs(userEvent.event.dt_start_time).format('YYYY-MM-DD') ?? '',
  );
  const [errorText, setErrorText] = useState('');
  const [interviewStartTime, setInterviewStartTime] = useState<number>(
    dayjs(userEvent.interview?.dt_start_time).valueOf() / 1000,
  );

  const { updateInterview, isUpdatingInterview } = useUpdateInterview({
    setErrorText,
    interviewStartTime,
  });

  const reset = () => {
    setStartTimestamp(null);
    setEndTimestamp(null);
    setFormattedDate('');
  };

  const handleSave = () =>
    updateInterview(
      {
        interview_id: userEvent.event.id,
        start_time: startTimestamp as number,
        end_time: endTimestamp as number,
        eventDate: formattedDate,
      },
      {
        onSuccess: () => {
          reset();
          handleClose();
          onSuccess();
          toast.success('Interview Invitation Saved');
        },
      },
    );

  useEffect(() => {
    if (userEvent) {
      setInterviewStartTime(
        dayjs(userEvent.interview?.dt_start_time).valueOf() / 1000,
      );
    }
  }, [userEvent]);

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <OpenInterview />
        <br />
        <Typography variant="h4" sx={{ p: 3 }}>
          {`${userEvent.application.applicant.name} - ${userEvent.application.position} Interview`}
        </Typography>
      </DialogTitle>
      {/* <Grid
        container
        sx={{ bgcolor: darkBlue2 }}
      >
        <Typography variant="h5" sx={{ color: white, p: 3 }}>

        </Typography>
      </Grid> */}
      <DialogContent>
        <Stack spacing={3} sx={{ marginBottom: 3 }}>
          {errorText && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {errorText}
            </Alert>
          )}
          <SelectDateTimeForm
            setStartTimestamp={setStartTimestamp!}
            setEndTimestamp={setEndTimestamp!}
            setFormattedDate={setFormattedDate!}
            defaultDuration={120}
            directionColumn
            notShowRepeat={true}
            initialValues={{
              date: dayjs(
                userEvent?.slot
                  ? userEvent?.slot?.slot_start_time
                  : userEvent?.block
                    ? userEvent?.block.dt_start_time
                    : userEvent.event.dt_start_time,
              ),
              startTime: dayjs(
                userEvent?.slot
                  ? userEvent?.slot?.slot_start_time
                  : userEvent?.block
                    ? userEvent?.block.dt_start_time
                    : userEvent.event.dt_start_time,
              ),
              endTime: dayjs(
                userEvent?.slot
                  ? userEvent?.slot?.slot_end_time
                  : userEvent?.block
                    ? userEvent?.block.dt_end_time
                    : userEvent.event.dt_end_time,
              ),
            }}
          />
          <LoadingButton
            disabled={!startTimestamp || !endTimestamp || !formattedDate}
            variant="contained"
            loadingPosition="start"
            loading={isUpdatingInterview}
            startIcon={<DoneIcon />}
            onClick={handleSave}
          >
            Save
          </LoadingButton>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateInterviewInvitationModal;
